import { useEffect } from "react";
import { Box, Button, Skeleton, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DashboardLayout from "layout";
import { useToast } from "components/Toast";
import { FieldLabel, fieldSx, primaryButtonSx } from "components/SideDrawer";
import { ExchangeRateConfig, getExchangeRate, updateExchangeRate } from "services/exchangeRate";
import { formatNaira, getApiError } from "services/finance";

const schema = yup.object({
  usd_ngn_rate: yup
    .number()
    .typeError("Enter a number")
    .moreThan(0, "Rate must be greater than 0")
    .required("Rate is required"),
  esim_markup_percent: yup
    .number()
    .typeError("Enter a number")
    .min(0, "Markup can't be negative")
    .required("Markup is required"),
});

type FormValues = yup.InferType<typeof schema>;

const EXAMPLE_USD = 10;

const ExchangeRate = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["exchangeRate"],
    queryFn: getExchangeRate,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<FormValues>({ resolver: yupResolver(schema) });

  useEffect(() => {
    if (data) {
      reset({
        usd_ngn_rate: data.usd_ngn_rate ?? ("" as any),
        esim_markup_percent: data.esim_markup_percent ?? ("" as any),
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: updateExchangeRate,
    onSuccess: (res) => {
      showToast(res?.message || "Exchange rate updated", "success");
      queryClient.invalidateQueries({ queryKey: ["exchangeRate"] });
    },
    onError: (error) => showToast(getApiError(error, "Failed to update exchange rate"), "error"),
  });

  const onSubmit = (values: FormValues) => {
    const body: ExchangeRateConfig = {};
    if (dirtyFields.usd_ngn_rate) body.usd_ngn_rate = values.usd_ngn_rate;
    if (dirtyFields.esim_markup_percent) body.esim_markup_percent = values.esim_markup_percent;
    mutation.mutate(body);
  };

  const rate = Number(watch("usd_ngn_rate")) || 0;
  const markup = Number(watch("esim_markup_percent")) || 0;
  const examplePrice = Math.ceil(EXAMPLE_USD * (1 + markup / 100) * rate);

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ width: "100%", p: { xs: 0, sm: 2 } }}>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={600} color="#101828">
            Exchange Rate
          </Typography>
          <Typography variant="body2" color="#475467">
            One USD → NGN rate for hotels and eSIM. The markup applies to eSIM only.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          bgcolor="#FFFFFF"
          border="1px solid #E4E7EC"
          borderRadius="12px"
          p="24px"
          maxWidth="520px"
          display="flex"
          flexDirection="column"
          gap="20px"
        >
          {isLoading ? (
            <>
              <Skeleton height={56} />
              <Skeleton height={56} />
            </>
          ) : (
            <>
              <Box>
                <FieldLabel>USD → NGN rate (₦ per $1)</FieldLabel>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ step: "any" }}
                  sx={fieldSx}
                  {...register("usd_ngn_rate")}
                  error={!!errors.usd_ngn_rate}
                  helperText={errors.usd_ngn_rate?.message}
                />
              </Box>
              <Box>
                <FieldLabel>eSIM markup (%)</FieldLabel>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ step: "any" }}
                  sx={fieldSx}
                  {...register("esim_markup_percent")}
                  error={!!errors.esim_markup_percent}
                  helperText={errors.esim_markup_percent?.message}
                />
              </Box>
              <Box bgcolor="#F9FAFB" borderRadius="8px" p="12px 16px">
                <Typography fontSize={13} color="#475467">
                  Example: a ${EXAMPLE_USD} Airalo plan sells for{" "}
                  <b>{formatNaira(examplePrice)}</b>
                </Typography>
                <Typography fontSize={12} color="#98A2B3" mt="4px">
                  Airalo price × (1 + markup%) × rate, rounded up.
                </Typography>
              </Box>
            </>
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={!isDirty || mutation.isPending}
            sx={primaryButtonSx}
          >
            {mutation.isPending ? "Saving..." : "Save changes"}
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ExchangeRate;
